import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { RecenzjaRozmowa, RecenzjaWskazanie } from "@shared/recenzjeTypy.js";
import { odmien } from "../../lib/polski.js";
import { ileWskazanOdPozycji, wskazaniaWgWypowiedzi, zakresyCytatow } from "../../lib/recenzja.js";
import { BRAK_WPISOW, ileUwagOdPozycji, ileWypowiedziNaStart, type WidokUwag } from "../../lib/uwagiWypowiedzi.js";
import { LegendaJakosci } from "../transcript/LegendaJakosci.js";
import { zaznaczeniaWTekscie, type ZakresCytatu, type ZakresZaznaczenia } from "../transcript/fragmenty.js";
import { WypowiedzRecenzji } from "./WypowiedzRecenzji.js";

/**
 * Ile wypowiedzi pokazujemy naraz, jak w transkrypcie aplikacji (components/transcript/Rozmowa.tsx): karta rosnie z trescia,
 * a dlugie rozmowy to setki dymkow. Porcje ida OD POCZATKU, bo transkrypt czyta sie od pierwszego zdania. Okno na starcie
 * siega jednak do najdalszej wypowiedzi, przy ktorej recenzent ma JAKAKOLWIEK uwage (`ileWypowiedziNaStart`), a napis
 * "Pokaz dalsze" mowi, ile jego uwag jest w czesci ukrytej.
 */
const PORCJA = 40;

/** Jak dlugo wypowiedz pokazana z karty "Powtarzane slowa" zostaje wyrozniona (jak w transkrypcie aplikacji). */
const CZAS_WYROZNIENIA_MS = 3000;

function ograniczonyRuch(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Czy po wysrodkowaniu dymka numer i mowca zostaja ponizej pasa zakrytego u gory okna (jak w Rozmowa.tsx). */
function numerWidacPoWysrodkowaniu(wiersz: HTMLElement, dymek: HTMLElement): boolean {
  const prostokat = dymek.getBoundingClientRect();
  const nadDymkiem = prostokat.top - wiersz.getBoundingClientRect().top;
  const zakryte = Number.parseFloat(window.getComputedStyle(wiersz).scrollMarginTop) || 0;
  return window.innerHeight / 2 - prostokat.height / 2 - nadDymkiem >= zakryte;
}

/**
 * Jak w transkrypcie aplikacji (Rozmowa.tsx): srodek dymka na srodek okna, a gdy numer i mowca wypadlyby wtedy pod
 * przypietym naglowkiem (dlugi dymek), poczatek calej wypowiedzi. Tu pod naglowkiem stoi jeszcze przypieta karta postepu
 * (.rec-sticky, RozmowaRecenzjiPage.tsx), a jej wysokosc sie zmienia (zawija sie na waskim ekranie, zmienia sie napis
 * stanu zapisu). Mierzymy ja wiec przy kazdym przewinieciu i podajemy liscie w zmiennej, z ktorej recenzja.css dolicza
 * ja do scroll-margin-top wypowiedzi. Bez karty (poligon) zmienna jest zerem.
 */
function przewinDoWypowiedzi(lista: HTMLElement, wiersz: HTMLElement): void {
  // jsdom nie implementuje scrollIntoView - brak metody nie moze wywrocic widoku.
  if (typeof wiersz.scrollIntoView !== "function") return;
  const karta = document.querySelector<HTMLElement>(".rec-sticky");
  lista.style.setProperty("--wysokosc-karty-postepu", `${karta ? karta.getBoundingClientRect().height : 0}px`);
  const behavior = ograniczonyRuch() ? "auto" : "smooth";
  const dymek = wiersz.querySelector<HTMLElement>(".bubble");
  if (dymek && numerWidacPoWysrodkowaniu(wiersz, dymek)) dymek.scrollIntoView({ block: "center", behavior });
  else wiersz.scrollIntoView({ block: "start", behavior });
}

/** Jedne, wspolne puste tablice: wiersz bez wskazan ani cytatow dostaje te same referencje, wiec `memo` go nie przerysowuje. */
const BRAK_ZAKRESOW: readonly ZakresCytatu[] = [];
const BRAK_WSKAZAN: readonly RecenzjaWskazanie[] = [];
const BRAK_ZAZNACZEN: readonly ZakresZaznaczenia[] = [];

/**
 * Transkrypt z wypowiedziami ponumerowanymi od 1, wskazaniami modelu i uwagami recenzenta pod wypowiedzia, z ktorej
 * pochodza. Stan pol (szkice) mieszka w samych wskazaniach i formularzach uwag. Wspolna jest tylko lista uwag
 * (`widok`), ktora zmienia sie, gdy wraca wynik zapisu: wiersze sa zapamietane i dostaja wpisy o stalej tozsamosci,
 * wiec zapis jednej uwagi albo zmiana licznika postepu nie przerysowuje setek dymkow.
 */
export const TranskryptRecenzji = memo(function TranskryptRecenzji({
  rozmowa,
  widok,
  skokDo = null,
}: {
  rozmowa: RecenzjaRozmowa;
  widok: WidokUwag;
  /**
   * Prosba o pokazanie wypowiedzi (przycisk "Wypowiedz N" w karcie "Powtarzane slowa"). `token` rosnie przy kazdej prosbie,
   * wiec ponowne klikniecie tej samej wypowiedzi tez przewija, a zwykle przerysowanie - nie. `trafienia`: miejsca slowa
   * w tej wypowiedzi, zaznaczane na czas wyroznienia; przewijamy do pierwszego z nich.
   */
  skokDo?: { numer: number; token: number; trafienia?: readonly ZakresZaznaczenia[] } | null;
}): React.JSX.Element {
  const [ile, setIle] = useState(() => ileWypowiedziNaStart(rozmowa.wypowiedzi, widok, PORCJA));
  const [wyroznionyNumer, setWyroznionyNumer] = useState<number | null>(null);
  const [zaznaczenia, setZaznaczenia] = useState<readonly ZakresZaznaczenia[]>(BRAK_ZAZNACZEN);
  const listaRef = useRef<HTMLOListElement>(null);
  /** Token ostatniej obsluzonej prosby o przewiniecie: rosnaca porcja nie ma przewijac drugi raz. */
  const obsluzonySkokRef = useRef(0);
  const { wgNumeru } = useMemo(() => wskazaniaWgWypowiedzi(rozmowa), [rozmowa]);
  const zakresy = useMemo(() => zakresyCytatow(rozmowa), [rozmowa]);

  // Ten sam mechanizm co w transkrypcie aplikacji (components/transcript/Rozmowa.tsx): rozszerz porcje do wypowiedzi,
  // przewin do jej dymku, wyroznij ja na chwile i przenies na nia fokus. Wypowiedz spoza porcji pojawia sie dopiero po
  // rozszerzeniu, wiec efekt uruchamia sie ponownie przy zmianie `ile`.
  useEffect(() => {
    if (!skokDo || skokDo.token === obsluzonySkokRef.current) return;
    const pozycja = rozmowa.wypowiedzi.findIndex((w) => w.numer === skokDo.numer);
    if (pozycja < 0) {
      obsluzonySkokRef.current = skokDo.token;
      return;
    }
    if (pozycja >= ile) {
      setIle(Math.ceil((pozycja + 1) / PORCJA) * PORCJA);
      return;
    }
    const lista = listaRef.current;
    const el = lista?.querySelector<HTMLElement>(`[data-numer="${skokDo.numer}"]`);
    if (!lista || !el) return;

    obsluzonySkokRef.current = skokDo.token;
    const doZaznaczenia = zaznaczeniaWTekscie(rozmowa.wypowiedzi[pozycja].tekst, skokDo.trafienia);
    // Srodkujemy DYMEK, nie cale <li>: pod dymkiem stoja wskazania i uwagi, ktore bywaja wyzsze od niego. Dlugi dymek idzie
    // do poczatku <li>, z numerem i mowca (przewinDoWypowiedzi). Gdy sa trafienia do zaznaczenia, przewija nastepny efekt,
    // juz do pierwszego z nich (pojawia sie dopiero po przerysowaniu).
    if (doZaznaczenia.length === 0) przewinDoWypowiedzi(lista, el);
    el.focus({ preventScroll: true });
    setWyroznionyNumer(skokDo.numer);
    setZaznaczenia(doZaznaczenia.length > 0 ? doZaznaczenia : BRAK_ZAZNACZEN);
  }, [skokDo, rozmowa.wypowiedzi, ile]);

  useEffect(() => {
    if (wyroznionyNumer === null || zaznaczenia.length === 0) return;
    const znak = listaRef.current?.querySelector<HTMLElement>(`[data-numer="${wyroznionyNumer}"] .trafienie-slowa`);
    // jsdom nie implementuje scrollIntoView - brak metody nie moze wywrocic widoku.
    if (znak && typeof znak.scrollIntoView === "function") {
      znak.scrollIntoView({ block: "center", behavior: ograniczonyRuch() ? "auto" : "smooth" });
    }
  }, [wyroznionyNumer, zaznaczenia]);

  useEffect(() => {
    if (wyroznionyNumer === null) return;
    const t = setTimeout(() => {
      setWyroznionyNumer(null);
      setZaznaczenia(BRAK_ZAZNACZEN);
    }, CZAS_WYROZNIENIA_MS);
    return () => clearTimeout(t);
  }, [wyroznionyNumer, skokDo?.token]);

  const widoczne = rozmowa.wypowiedzi.slice(0, ile);
  const zostalo = rozmowa.wypowiedzi.length - widoczne.length;
  const wskazanZostalo = zostalo > 0 ? ileWskazanOdPozycji(rozmowa.wypowiedzi, wgNumeru, widoczne.length) : 0;
  const uwagZostalo = zostalo > 0 ? ileUwagOdPozycji(rozmowa.wypowiedzi, widok, widoczne.length) : 0;
  const wUkrytych = [
    wskazanZostalo > 0 ? `${wskazanZostalo} ${odmien(wskazanZostalo, "wskazanie", "wskazania", "wskazań")}` : null,
    uwagZostalo > 0 ? `${uwagZostalo} ${odmien(uwagZostalo, "Twoja uwaga", "Twoje uwagi", "Twoich uwag")}` : null,
  ].filter((czesc): czesc is string => czesc !== null);

  return (
    <div className="rozmowa-wrap rec-rozmowa-wrap">
      {/* Ta sama legenda co w transkrypcie aplikacji, razem z opisem zaznaczen cytatow (LegendaJakosci.tsx). */}
      <LegendaJakosci />

      <ol className="rozmowa rec-rozmowa" role="list" ref={listaRef}>
        {widoczne.map((wypowiedz) => (
          <WypowiedzRecenzji
            key={wypowiedz.numer}
            wypowiedz={wypowiedz}
            mowca={rozmowa.mowcy[wypowiedz.mowca]}
            zakresy={zakresy.get(wypowiedz.numer) ?? BRAK_ZAKRESOW}
            wskazania={wgNumeru.get(wypowiedz.numer) ?? BRAK_WSKAZAN}
            wpisy={widok.get(wypowiedz.numer) ?? BRAK_WPISOW}
            wyrozniona={wypowiedz.numer === wyroznionyNumer}
            zaznaczenia={wypowiedz.numer === wyroznionyNumer ? zaznaczenia : BRAK_ZAZNACZEN}
          />
        ))}
      </ol>

      {zostalo > 0 && (
        <div className="rec-wiecej">
          <button type="button" className="rozmowa-wiecej" onClick={() => setIle((n) => n + PORCJA)}>
            {`Pokaż dalsze wypowiedzi (zostało ${zostalo}${wUkrytych.length > 0 ? `, w nich ${wUkrytych.join(", ")}` : ""})`}
          </button>
          <button type="button" className="rozmowa-wiecej" onClick={() => setIle(rozmowa.wypowiedzi.length)}>
            Pokaż wszystkie wypowiedzi
          </button>
        </div>
      )}
    </div>
  );
});
